import { useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { ListingsIcon } from 'assets/images/icons';
import { LiveChatIcon } from 'assets/images/channels';
import { useTranslation, useChannels } from '@hooks';
import Button from '@components/Button';
import { Typography } from '@components/Typography';
import { contactsActions } from '@actions';
import { RoutePath } from '@constants/routes';

const Container = styled('div', {
    shouldForwardProp: prop => prop !== 'hasResponse',
})(({ hasResponse }) => ({
    display: 'flex',
    width: '100%',
    justifyContent: hasResponse ? 'space-between' : 'flex-end',
    flexWrap: 'wrap',
    '@media (max-width: 600px)': {
        flexDirection: 'column',
    },
}));

const TicketInfoWrapper = styled('div')({
    display: 'flex',
    gap: '16px',
    '@media (max-width: 600px)': {
        gap: '0',
        flexDirection: 'column',
    },
});

const TicketInfo = styled('div')({
    display: 'flex',
    justifyContent: 'space-between',
    '@media (max-width: 1200px)': {
        flexWrap: 'wrap',
        justifyContent: 'flex-start',
    },
});

const TicketLabel = styled('div')({
    marginRight: '16px',
    maxWidth: '172px',
    minWidth: '75px',
    alignItems: 'flex-start',
    display: 'flex',
    flexDirection: 'column',
    '@media (max-width: 1200px)': {
        maxWidth: '150px',
        minWidth: '100px',
        marginBottom: '16px',
        '&:first-of-type': {
            flex: 1,
        },
    },
});

const FeedbackToolbar = ({ data, onClickTicket }) => {
    const users = useSelector(state => state.account?.account?.users || []);

    const { t } = useTranslation();
    const history = useHistory();
    const dispatch = useDispatch();
    const { getChannelsByLocationId, checkTwoWaySms, checkWhatsAppStatus, checkTwoWayLiveChat } = useChannels();
    const {
        id,
        ticket = null,
        conversationStatus,
        conversationAssigneeName,
        contactId,
        phoneNumber,
        email,
        locationId,
        conversationId,
    } = data;

    const hasResponse = Boolean(ticket) || Boolean(conversationStatus !== null);

    const handleClickTicket = useCallback(
        type => onClickTicket({ id, ticket, type, locationId }),
        [id, ticket, locationId],
    );

    const handleClickConversation = () =>
        conversationId
            ? history.push(`${RoutePath.MessagingInbox}?conversationId=${conversationId}`)
            : dispatch(
                  contactsActions.setContactInitConversationModal({
                      open: true,
                      locations: [locationId],
                      contactId,
                      ctx: { ...data, externalId: data?.contactExternalId, surveyResponseId: data?.id },
                  }),
              );

    const assignee = users.find(user => user.id === ticket?.assigneeId) || { firstName: '', lastName: '' };
    const ticketStatusMap = {
        0: t('FeedbackToolbar.empty'),
        1: t('common.actionRequired'),
        2: t('common.inProgress'),
        3: t('FeedbackToolbar.completed'),
    };
    const conversationStatusMap = {
        0: t('InboxPage.open'),
        1: t('InboxPage.resolved'),
    };

    const availableChannels = useMemo(() => {
        const filterCb = c => checkTwoWaySms(c) || checkWhatsAppStatus(c) || checkTwoWayLiveChat(c);
        return getChannelsByLocationId(data.locationId, filterCb);
    }, [data]);

    const isConversationButtonAvailable =
        (availableChannels.length > 0 && contactId && (phoneNumber || email)) || conversationId;

    return (
        <Container hasResponse={hasResponse}>
            <TicketInfoWrapper>
                {conversationId && (
                    <TicketInfo>
                        <TicketLabel>
                            <Typography
                                variant="caption"
                                sx={{ display: 'inline-block', marginBottom: '2px', color: 'rgba(0, 0, 0, 0.6)' }}
                            >
                                {t('common.assignedTo')}:
                            </Typography>
                            <Typography variant="body2" style={{ color: 'rgba(0, 0, 0, 0.87)' }}>
                                {conversationAssigneeName}
                            </Typography>
                        </TicketLabel>
                        <TicketLabel>
                            <Typography
                                variant="caption"
                                sx={{ display: 'inline-block', marginBottom: '2px', color: 'rgba(0, 0, 0, 0.6)' }}
                            >
                                {t('common.status')}:
                            </Typography>
                            <Typography variant="body2" style={{ color: 'rgba(0, 0, 0, 0.87)' }}>
                                {conversationStatusMap[conversationStatus]}
                            </Typography>
                        </TicketLabel>
                    </TicketInfo>
                )}
                {ticket && (
                    <TicketInfo>
                        <TicketLabel>
                            <Typography
                                variant="caption"
                                sx={{ display: 'inline-block', marginBottom: '2px', color: 'rgba(0, 0, 0, 0.6)' }}
                            >
                                {t('FeedbackToolbar.ticketAssignedTo')}
                            </Typography>
                            <Typography variant="body2" style={{ color: 'rgba(0, 0, 0, 0.87)' }}>
                                {`${assignee?.firstName} ${assignee?.lastName}`}
                            </Typography>
                        </TicketLabel>
                        <TicketLabel>
                            <Typography
                                variant="caption"
                                sx={{ display: 'inline-block', marginBottom: '2px', color: 'rgba(0, 0, 0, 0.6)' }}
                            >
                                {t('FeedbackToolbar.ticketStatus')}
                            </Typography>
                            <Typography variant="body2" style={{ color: 'rgba(0, 0, 0, 0.87)' }}>
                                {ticketStatusMap[ticket?.ticketStatus]}
                            </Typography>
                        </TicketLabel>
                    </TicketInfo>
                )}
            </TicketInfoWrapper>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {isConversationButtonAvailable && (
                    <Button variant="outlined" onClick={handleClickConversation}>
                        <LiveChatIcon style={{ width: '1rem', height: '1rem', marginRight: '8px' }} />
                        {conversationId
                            ? t('InitConversation.viewConversation')
                            : t('InitConversation.newConversation')}
                    </Button>
                )}
                <Button
                    variant="outlined"
                    onClick={() => (ticket ? handleClickTicket('view') : handleClickTicket('create'))}
                >
                    <ListingsIcon style={{ width: '1rem', height: '1rem', marginRight: '8px' }} />
                    {ticket ? t('buttons.viewTicket') : t('buttons.newTicket')}
                </Button>
            </div>
        </Container>
    );
};

export default FeedbackToolbar;
